<script setup lang="ts">
import { mdiImageOffOutline } from "@mdi/js";
import { computed, ref, warn, watch, type ImgHTMLAttributes } from "vue";
import type { ClassProps } from "../../lib/base-props";
import { deprecatedSizeToCSS, type DeprecatedCustomSizeProps } from "../../lib/size";
import { cn } from "../../lib/utils";
import Icon from "../icon/Icon.vue";
import RadialProgress from "../progress/RadialProgress.vue";

interface Props
	extends ClassProps,
		DeprecatedCustomSizeProps,
		/* @vue-ignore */ Omit<ImgHTMLAttributes, "src" | "loading"> {
	src?: string;
	/**
	 * Force the loading state. Usefull, if a request if needed before having the src.
	 */
	loading?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
	(evt: "load"): void;
	(evt: "error", err: string | Event): void;
}>();

defineSlots<{
	loading(): void;
	placeholder(): void;
}>();

const style = computed(() => {
	const style = deprecatedSizeToCSS(props);
	if (__DEV__ && Object.keys(style).length > 0) {
		warn("[UI] Using deprecated size props. Please use tailwindcss utility classes instead.");
	}

	return style;
});

const image = new Image();

const internalLoading = ref(false);
const loadedSrc = ref("");

const errored = ref(false);

watch(
	() => props.src,
	(src) => {
		loadedSrc.value = "";
		if (src) {
			errored.value = false;
			internalLoading.value = true;
			image.src = src;
		}
	},
	{ immediate: true }
);

image.onload = async () => {
	await image.decode();

	loadedSrc.value = image.currentSrc || image.src;
	internalLoading.value = false;
};
image.onerror = (err) => {
	internalLoading.value = false;
	errored.value = true;
	emit("error", err);
};
</script>

<template>
	<div :class="cn('flex-center size-full overflow-clip', props.class)" :style="style">
		<slot v-if="internalLoading || loading" name="loading">
			<RadialProgress class="aspect-square h-1/2" color="primary" />
		</slot>
		<slot v-else-if="errored || !loadedSrc" name="placeholder">
			<Icon class="size-full" :d="mdiImageOffOutline" />
		</slot>
		<img v-else class="h-full object-contain" :src="loadedSrc" v-bind="$attrs" />
	</div>
</template>
